"use client";

import { CheckIcon } from "@radix-ui/react-icons";
import {
  Box,
  ChevronDownIcon,
  Flex,
  Text,
  VisuallyHidden,
} from "@radix-ui/themes";
import type { OrganizationInfo } from "../api/endpoint.js";
import cx from "clsx";
import {
  getDomProps,
  isPromiseLike,
  type WidgetRootDomProps,
  type WidgetRootState,
} from "./utils.js";
import { getErrorMessage } from "./generic-error.js";
import {
  Button,
  type ButtonProps,
  Skeleton,
  DropdownMenu,
} from "./elements.js";
import { unstable_useWidgetsInvalidator as useWidgetsInvalidator } from "../utils.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

type OrganizationSwitcherVariant = "ghost" | "outline";

// Rename all uses of `org` to `organization`
interface OrganizationSwitcherPassthroughProps extends WidgetRootDomProps {
  switchToOrganization: ({
    organizationId,
  }: {
    organizationId: string;
  }) => void | Promise<void>;
  // Simple props to affect the overall style
  variant?: OrganizationSwitcherVariant;
  organizationLabel?: string | null;
  children?: React.ReactNode;
  /**
   * Choose where to truncate organization name in the trigger and dropdown
   * items.
   *
   * - `right`: Truncate the right side of the organization name
   * - `middle`: Truncate the middle of the organization name, trying to keep
   *  words whole
   */
  truncateBehavior?: "right" | "middle";
}

interface OrganizationSwitcherProps extends OrganizationSwitcherPassthroughProps {
  organizations: OrganizationInfo[];
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  organizations,
  switchToOrganization,
  variant,
  organizationLabel = "Organizations",
  truncateBehavior = "right",
  children,
  ...domProps
}) => {
  const currentOrganization = organizations.find(
    (organization) => organization.current,
  );
  const invalidateAllWidgets = useWidgetsInvalidator();

  // TODO: Possible if the user has no organizations - we should figure out what
  // to do in this case
  if (!currentOrganization) {
    return null;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button
          variant="secondary"
          {...mapVariantProps(variant)}
          {...getWidgetRootDomProps("resolved", {
            ...domProps,
            // TODO: Remove `OrganizationSwitcherTrigger` in the next major
            // version. This should follow conventions of all other widgets
            // using classnames in getWidgetRootDomProps.
            className: cx(domProps.className, "OrganizationSwitcherTrigger"),
          })}
        >
          <Flex
            align="center"
            justify="between"
            gap="2"
            flexGrow="1"
            overflow="hidden"
            minWidth="0"
          >
            <TruncatedOrganizationName
              organizationName={currentOrganization.name}
              truncateBehavior={truncateBehavior}
            />
            <Flex asChild flexShrink="0">
              <DropdownMenu.TriggerIcon />
            </Flex>
          </Flex>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.Group>
          {organizationLabel ? (
            <DropdownMenu.Label>
              <Text>{organizationLabel}</Text>
            </DropdownMenu.Label>
          ) : null}
          {organizations.map((organization) => (
            <Flex
              key={organization.id}
              asChild
              pr="2"
              maxWidth="280px"
              minWidth="180px"
            >
              <DropdownMenu.Item
                onClick={() => {
                  if (organization.id !== currentOrganization.id) {
                    const result = switchToOrganization({
                      organizationId: organization.id,
                    });
                    if (isPromiseLike(result)) {
                      result.then(invalidateAllWidgets);
                    } else {
                      invalidateAllWidgets();
                    }
                  }
                }}
              >
                <Flex
                  justify="between"
                  align="center"
                  gap="4"
                  flexGrow="1"
                  overflow="hidden"
                >
                  <TruncatedOrganizationName
                    organizationName={organization.name}
                    truncateBehavior={truncateBehavior}
                  />
                  {organization.current && (
                    <VisuallyHidden>
                      <Translation
                        defaultMessage="(current)"
                        id="daFXTP"
                        description="Label for screen readers indicating current organization"
                      />
                    </VisuallyHidden>
                  )}
                  <Flex
                    aria-hidden
                    align="center"
                    justify="center"
                    flexShrink="0"
                  >
                    {organization.current ? (
                      <CheckIcon width="18px" height="18px" />
                    ) : (
                      // make the extra space for
                      <Box width="18px" height="18px" />
                    )}
                  </Flex>
                </Flex>
              </DropdownMenu.Item>
            </Flex>
          ))}
        </DropdownMenu.Group>
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

interface OrganizationSwitcherLoadingProps extends WidgetRootDomProps {
  variant?: OrganizationSwitcherVariant;
}

const OrganizationSwitcherLoading: React.FC<
  OrganizationSwitcherLoadingProps
> = ({ variant, ...props }: OrganizationSwitcherLoadingProps) => {
  return (
    // Always need DropdownMenu.Root to wrap children than may include
    <Button
      variant="secondary"
      disabled
      {...mapVariantProps(variant)}
      {...getWidgetRootDomProps("loading", props)}
    >
      <Flex align="center" gap="2">
        <Skeleton>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <Text>Loading...</Text>
        </Skeleton>
        <ChevronDownIcon />
      </Flex>
    </Button>
  );
};

interface OrganizationSwitcherErrorProps extends WidgetRootDomProps {
  error: unknown;
  variant?: OrganizationSwitcherVariant;
}

function OrganizationSwitcherError({
  error,
  variant,
  ...domProps
}: OrganizationSwitcherErrorProps) {
  const translate = useTranslation();
  const { heading } = getErrorMessage(error, translate);

  return (
    <Button
      variant="secondary"
      disabled
      {...mapVariantProps(variant)}
      {...getWidgetRootDomProps("error", domProps)}
    >
      <Flex align="center" gap="2">
        <Text>{heading}</Text>
        <ChevronDownIcon />
      </Flex>
    </Button>
  );
}

const MAX_TRUNCATED_SUFFIX_LENGTH = 10;
const MIN_TRUNCATED_SUFFIX_LENGTH = 3;

const WHITE_SPACE_REGEX = /\s/;

const getSplitPosition = (organizationName: string) => {
  if (organizationName.length <= MAX_TRUNCATED_SUFFIX_LENGTH) {
    return organizationName.length;
  }

  for (
    let i = organizationName.length - MAX_TRUNCATED_SUFFIX_LENGTH;
    i < organizationName.length - MIN_TRUNCATED_SUFFIX_LENGTH;
    i++
  ) {
    if (WHITE_SPACE_REGEX.test(organizationName[i - 1])) {
      return i;
    }
  }

  return organizationName.length - MAX_TRUNCATED_SUFFIX_LENGTH;
};

const splitOrganizationName = (organizationName: string) => {
  const splitPosition = getSplitPosition(organizationName);
  return [
    organizationName.slice(0, splitPosition),
    organizationName.slice(splitPosition),
  ];
};

const TruncatedOrganizationName = ({
  organizationName,
  truncateBehavior,
}: {
  organizationName: string;
  truncateBehavior: "right" | "middle";
}) => {
  if (truncateBehavior === "right") {
    return <Text truncate>{organizationName}</Text>;
  }

  const [organizationNameLeft, organizationNameRight] =
    splitOrganizationName(organizationName);

  return (
    <Flex overflow="hidden">
      <VisuallyHidden>
        <Text>{organizationName}</Text>
      </VisuallyHidden>
      <Text aria-hidden truncate style={{ whiteSpace: "pre" }}>
        {organizationNameLeft}
      </Text>
      <Text aria-hidden>{organizationNameRight}</Text>
    </Flex>
  );
};

function mapVariantProps(variant?: OrganizationSwitcherVariant): ButtonProps {
  const variantProps: ButtonProps = {};
  // Passing `undefined` to the variant prop will result in overrides to props we
  // set internally. This should be addressed in Radix Themes, but we can
  // explicitly set it only when it's provided and _not_ undefined.
  if (variant) {
    variantProps.unsafe_radixVariant = variant;
  }
  return variantProps;
}

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "organization-switcher",
    widgetState: state,
  });
}

export type {
  OrganizationSwitcherProps,
  OrganizationSwitcherLoadingProps,
  OrganizationSwitcherErrorProps,
  /** @internal */
  OrganizationSwitcherPassthroughProps,
};
export {
  OrganizationSwitcher,
  OrganizationSwitcherLoading,
  OrganizationSwitcherError,
};
